import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, FormControl } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AddPhotoIcon, PlaneIcon, SearchIcon, XIcon } from '../../components/Icons';
import Loader from '../../components/Loader';
import Message from './Message';
import { selectAuthUser, selectChatToken } from '../../reducers/auth/auth.selectors';
import chatService from '../../chatService';
import AsyncSelect from 'react-select/async';
import API from '../../API';
import Conversations from './Conversations';
import { LoadMoreButton } from 'components/generic-components/LoadMoreButton';

const Messages = () => {
  const { t } = useTranslation();
  const user = useSelector(selectAuthUser);
  const scrollBottomRef = useRef(null);
  const [searchRecipient, setSearchRecipient] = useState('');
  const [search, setSearch] = useState(null);
  const [activeChat, setActiveChat] = useState(null);
  const [activeChatMessages, setActiveChatMessages] = useState([]);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [hasMoreConversations, setHasMoreConversations] = useState(false);
  const [text, setText] = useState('');
  const chatToken = useSelector(selectChatToken);
  const isScrollAtTop = node => {
    return node.scrollTop < 150;
  };
  const isScrollAtBottom = node => {
    return node.scrollHeight - (node.scrollTop + node.clientHeight) < 150;
  };
  const scrollToBottom = () => {
    scrollBottomRef.current.scrollTop = scrollBottomRef.current.scrollHeight;
  };
  useEffect(() => {
    console.log(chatToken, chatService.initialized);
    if (chatToken && chatService.initialized) {
      loadConversationsAndSubscribe();
      chatService.subscribeToMessages();
    }
    return () => {
      chatService.resetConversationsSubscriptions();
      chatService.resetActiveConversationSubscriptions();
    };
  }, [chatToken]);
  const loadConversationsAndSubscribe = () => {
    chatService.resetConversationsSubscriptions();
    chatService.loadConversationsAndSubscribe(onConversationsUpdate).then(conversations => {
      if (conversations.length === chatService.limit) {
        setHasMoreConversations(true);
      }
    });
  };
  const onConversationsUpdate = useCallback(conversationsUpdate => {
    console.log('onConversationsUpdate', conversationsUpdate);
    setConversations(conversationsUpdate);
    setIsLoadingConversations(false);
  }, []);
  const handleConversationNewMessage = useCallback(messages => {
    console.log('handleConversationNewMessage', messages);
    setActiveChatMessages(messages);
    scrollToBottom();
  }, []);
  const loadMoreConversations = () => {
    setIsLoadingConversations(true);
    chatService.loadConversations().then(conversations => {
      if (conversations.length < chatService.limit) {
        setHasMoreConversations(false);
      }
      setIsLoadingConversations(false);
    });
  };
  const loadMoreMessages = () => {
    setIsLoadingMessages(true);
    chatService.getConversationMessages(activeChat.uid).then(messages => {
      if (messages.length < chatService.limit) {
        setHasMoreMessages(false);
      }
      setActiveChatMessages([...messages, ...activeChatMessages]);
      setIsLoadingMessages(false);
    });
  };
  const selectConversationFromList = conversation => {
    console.log('selectConversationFromList', conversation, activeChat);
    const uid = conversation.conversationWith.uid;
    if (!activeChat || activeChat === 'new' || activeChat === 'empty' || activeChat.uid !== uid) {
      setActiveChat({
        uid: uid,
        name: conversation.conversationWith.name,
        nickname:
          conversation.conversationWith.metadata && conversation.conversationWith.metadata.nickname
            ? conversation.conversationWith.metadata.nickname
            : null,
      });
      setIsLoadingMessages(true);
      setActiveChatMessages([]);
      chatService.getConversationMessages(uid).then(messages => {
        setActiveChatMessages(messages);
        if (messages.length === chatService.limit) {
          setHasMoreMessages(true);
        } else {
          setHasMoreMessages(false);
        }
        if (!!messages.length && conversation.unreadMessageCount > 0) {
          chatService.markAsRead(messages[messages.length - 1].id, uid);
          chatService.updateConversationUnreadCount(conversation, 0);
        }
        setIsLoadingMessages(false);
        chatService.subscribeToConversationMessages(conversation.conversationId, handleConversationNewMessage);
        scrollToBottom();
      });
    }
  };

  const getSelectUsetList = inputValue => {
    return API.post('profiles/search/chat', { query: inputValue }).then(resp => {
      return resp.map(el => {
        return { value: el, label: el.name };
      });
    });
  };
  const sendMessage = () => {
    if (activeChat && text) {
      let uid = 0;
      if (activeChat === 'new') {
        if (searchRecipient) {
          if (searchRecipient === 'all') {
            setIsLoadingConversations(true);
            setConversations([]);
            setActiveChat('');
            return API.post('send_msg_to_followers', { text })
              .then(resp => {
                setActiveChat('');
                setText('');
                setSearchRecipient('');
                return true;
              })
              .catch(err => {
                setActiveChat('new');
                return false;
              })
              .finally(() => {
                loadConversationsAndSubscribe();
              });
          }
          uid = searchRecipient.id;
        } else {
          return false;
        }
      } else if (activeChat === 'empty') {
        if (search) {
          uid = search.id;
        } else {
          return false;
        }
      } else {
        uid = activeChat.uid;
      }
      console.log(uid, search);
      chatService.sendTextMessage(uid, text).then(({ message, conversation }) => {
        console.log('sendMessage', activeChat, message, conversation);
        if (activeChat === 'new' || activeChat === 'empty') {
          //create conversation or select from list
          selectConversationFromList(conversation);
        }
        scrollToBottom();
      });
      setText('');
    }
  };
  const handleFileSelect = e => {
    if (activeChat && activeChat.uid) {
      const file = Array.from(e.target.files);
      console.log(file);
      chatService.sendImageMessage(activeChat.uid, file[0]);
    }
  };
  const setActiveChatNew = () => {
    setActiveChat('new');
    setSearchRecipient('');
    //reset chat subscription
    chatService.resetActiveConversationSubscriptions();
  };
  const handleConversationSearch = val => {
    console.log(val);
  };
  useEffect(() => {
    if (search) {
      setIsLoadingMessages(true);
      setActiveChatMessages([]);
      chatService.getConversationWithUid(search.id).then(conv => {
        if (conv) {
          selectConversationFromList(conv);
        } else {
          setActiveChat('empty');
          setIsLoadingMessages(false);
        }
      });
    }
  }, [search]);
  return (
    <div className="col p-0 messages-cont d-flex ">
      <Conversations
        conversations={conversations}
        isLoadingConversations={isLoadingConversations}
        selectConversationFromList={selectConversationFromList}
        hasMoreConversations={hasMoreConversations}
        getSelectUsetList={getSelectUsetList}
        loadMoreConversations={loadMoreConversations}
        search={search}
        setSearch={setSearch}
        setActiveChatNew={setActiveChatNew}
      />
      <div className="chat-cont ">
        <div className="chat-messages-cont ">
          {!!activeChat ? (
            <>
              {activeChat === 'new' ? (
                <div className="my-auto">
                  <div className="d-flex">
                    <XIcon
                      width={24}
                      onClick={() => {
                        setActiveChat(null);
                        setSearchRecipient('');
                      }}
                      className="cursor-pointer"
                    />
                    <p
                      className="text-center text-uppercase flex-grow-1 text-light-gray"
                      style={{ padding: '0 40px 0 20px' }}
                    >
                      {t('select user to start new message')}
                    </p>
                  </div>
                  <hr className="my-1" />
                  <div className="row no-gutters">
                    <div className="d-flex align-items-center col-lg-6 col-12">
                      <AsyncSelect
                        cacheOptions
                        loadOptions={getSelectUsetList}
                        placeholder={t('Add New Recipient')}
                        onChange={e => setSearchRecipient(e.value)}
                        components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                        isDisabled={searchRecipient === 'all'}
                        styles={{
                          control: provided => ({
                            ...provided,
                            border: 'none !important',
                            boxShadow: 'none',
                          }),
                          container: provided => ({ ...provided, flex: 1 }),
                        }}
                      />

                      <SearchIcon width={20} style={{ margin: '0px 0.75rem' }} className="cursor-pointer" />
                    </div>
                    <div className="col-12 col-lg-6 d-flex align-items-center ">
                      <Form.Check
                        className="cursor-pointer ml-2"
                        type="checkbox"
                        id="age-terms"
                        name="agreed"
                        checked={searchRecipient === 'all'}
                        onChange={e => {
                          if (searchRecipient === 'all') setSearchRecipient('');
                          else setSearchRecipient('all');
                        }}
                        label={t('Or Send to all followers')}
                      />
                    </div>
                  </div>
                  <hr className="my-1" />
                </div>
              ) : activeChat === 'empty' ? (
                <div className="my-auto">
                  <div className="d-flex">
                    <XIcon width={24} onClick={() => setActiveChat(null)} className="cursor-pointer" />
                    <p className="text-center flex-grow-1 text-light-gray" style={{ padding: '0 40px 0 20px' }}>
                      {t('No messages with')}: {!!search && search.name}
                    </p>
                  </div>
                  <hr className="my-1" />
                  <p className="text-center">{t('Start messaging now')}</p>
                </div>
              ) : (
                <Loader isLoadin={isLoadingMessages && activeChatMessages.length === 0}>
                  <div className="text-center conversation-with text-light-gray p-2">
                    {t('CONVERSATION WITH')}: {activeChat.name}{' '}
                    {/*{'\u00A0'}
                    {activeChat.nickname && '@' + activeChat.nickname}*/}
                  </div>
                  <div className="px-3 flex-grow-1" style={{ overflowY: 'scroll' }} ref={scrollBottomRef}>
                    {hasMoreMessages && (
                      <Loader isLoadin={isLoadingMessages} height={36} size={28}>
                        <LoadMoreButton handleLoadMore={loadMoreMessages} />
                      </Loader>
                    )}
                    {activeChatMessages.map(el => (
                      <Message
                        key={el.id}
                        isMe={user.id === Number(el.receiverId) ? true : false}
                        text={el.data.text}
                        attachments={el.data.attachments ? el.data.attachments : null}
                        scrollToBottom={scrollToBottom}
                        author={el.sender}
                      />
                    ))}
                    <div className="d-none" ref={scrollBottomRef}></div>
                  </div>
                </Loader>
              )}

              <div className="compose-msg border-top pb-4 mt-2 mx-3">
                <div className="form-group w-100">
                  <FormControl
                    as="textarea"
                    type="text"
                    className="form-control"
                    id="text"
                    name="text"
                    value={text}
                    onKeyDown={e => {
                      if (e.keyCode == 13 && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    onChange={e => {
                      setText(e.target.value);
                    }}
                    style={{ resize: 'none', border: 'none' }}
                    placeholder={t('Compose message...')}
                  />
                </div>
                <div className="d-flex align-items-center">
                  <Form.File id="formcheck-api-custom" className=" pl-3">
                    <Form.File.Input
                      accept=".jpg,.jpeg,.webp,.gif,.bmp,.png"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                    <Form.Label data-browse="Upload">
                      <AddPhotoIcon width={27} className="cursor-pointer" />
                    </Form.Label>
                  </Form.File>
                  <button
                    type="submit"
                    className="btn btn-primary  ml-auto text-uppercase text-white "
                    style={{
                      borderRadius: '32px',
                      fontSize: '1.2rem',
                      padding: '0.5rem 1.3rem 0.5rem 1rem',
                    }}
                    onClick={sendMessage}
                  >
                    <PlaneIcon height="1.5rem" fill="#fff" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="d-flex align-items-center justify-content-center flex-grow-1 flex-column">
              <p className="text-uppercase text-light-gray mb-3 text-center">
                {t('select conversation from the left to view')}
                <br />
                {t(' or start new message')}
              </p>
              <button
                type="submit"
                className="btn btn-dark   text-uppercase text-white d-flex "
                style={{ borderRadius: '32px', fontSize: '0.9rem', padding: '0.5rem 1.3rem 0.5rem 1rem' }}
                onClick={setActiveChatNew}
              >
                {t('new message')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;
